import React, { useState, useRef } from "react";
import useSWR, { mutate } from "swr";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, Play, RefreshCw, Trash2, Pencil, TestTube2, Loader2, ShieldAlert,
  CheckCircle2, XCircle, Clock, ScanEye, Radio, History as HistoryIcon,
  Download, Upload, KeyRound,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const fetcher = (url) => api.get(url).then((r) => r.data);

const STATUS_STYLES = {
  "Connected": { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: CheckCircle2 },
  "Success": { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: CheckCircle2 },
  "Session Active": { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: CheckCircle2 },
  "Running": { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", icon: Loader2 },
  "Failed": { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", icon: XCircle },
  "Login Attention Required": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-300", icon: ShieldAlert },
  "MFA Required": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-300", icon: ShieldAlert },
  "CAPTCHA Detected": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-300", icon: ShieldAlert },
  "Disabled": { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-200", icon: XCircle },
  "Never Tested": { bg: "bg-gray-50", text: "text-gray-500", border: "border-gray-200", icon: Clock },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES["Never Tested"];
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${s.bg} ${s.text} ${s.border}`}
          data-testid={`status-${(status || 'unknown').toLowerCase().replace(/ /g,'-')}`}>
      <Icon className={`w-3 h-3 ${status === 'Running' ? 'animate-spin' : ''}`} /> {status}
    </span>
  );
};

const AccountForm = ({ open, onOpenChange, editing, onSaved, user, companies }) => {
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const isOwner = user?.role === "platform_owner";
  const initialCompany = editing?.company_id || (isOwner && companies?.length === 1 ? companies[0].id : "");
  const [form, setForm] = useState(() => ({
    account_name: editing?.account_name || "",
    company_id: initialCompany,
    username: "",
    password: "",
    payments_url: editing?.payments_url || "https://myportal.drivers.dpd.co.uk/manage-account#/payments",
    timezone: editing?.timezone || "Europe/London",
    schedule_time: editing?.schedule_time || "22:00",
    automation_enabled: editing?.automation_enabled ?? true,
    notes: editing?.notes || "",
  }));

  React.useEffect(() => {
    if (open) {
      setForm({
        account_name: editing?.account_name || "",
        company_id: editing?.company_id || (isOwner && companies?.length === 1 ? companies[0].id : ""),
        username: "",
        password: "",
        payments_url: editing?.payments_url || "https://myportal.drivers.dpd.co.uk/manage-account#/payments",
        timezone: editing?.timezone || "Europe/London",
        schedule_time: editing?.schedule_time || "22:00",
        automation_enabled: editing?.automation_enabled ?? true,
        notes: editing?.notes || "",
      });
    }
  }, [open, editing, isOwner, companies]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!editing && (!form.username || !form.password)) {
      toast.error("Username and password are required");
      return;
    }
    if (!form.account_name.trim()) {
      toast.error("Account name is required");
      return;
    }
    if (isOwner && !editing && !form.company_id) {
      toast.error("Please select which company this account belongs to");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      // If editing and username/password blank, drop them so backend keeps existing
      if (editing) {
        if (!payload.username) delete payload.username;
        if (!payload.password) delete payload.password;
      }
      // Company Admin doesn't need company_id (backend uses their own)
      if (!isOwner) delete payload.company_id;
      let saved;
      if (editing) {
        saved = (await api.patch(`/integrations/dpd/accounts/${editing.id}`, payload)).data;
      } else {
        saved = (await api.post("/integrations/dpd/accounts", payload)).data;
      }
      toast.success(editing ? "Account updated" : "Account created");
      onSaved(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="account-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit DPD Account" : "Add DPD Account"}</DialogTitle>
          <DialogDescription>
            {editing
              ? "Leave the password blank to keep the current one. Changing the password will invalidate the saved session."
              : "Credentials are encrypted with your server key and never sent back to the browser."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">Account name</Label>
            <Input value={form.account_name} onChange={(e) => set("account_name", e.target.value)}
                   placeholder="Newtown Main Account" data-testid="acc-name" className="mt-1.5" />
          </div>
          {isOwner && !editing && (
            <div>
              <Label className="text-xs uppercase tracking-wider">Company</Label>
              <select
                value={form.company_id}
                onChange={(e) => set("company_id", e.target.value)}
                data-testid="acc-company"
                className="mt-1.5 w-full h-10 rounded-md border border-brand-border bg-white px-3 text-sm"
              >
                <option value="">— Select company —</option>
                {(companies || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider">
              DPD username {editing && <span className="text-brand-text-muted normal-case">(blank to keep current: {editing.username_masked})</span>}
            </Label>
            <Input value={form.username} onChange={(e) => set("username", e.target.value)}
                   placeholder={editing?.username_masked || "you@example.com"}
                   autoComplete="off" data-testid="acc-username" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">
              DPD password {editing && <span className="text-brand-text-muted normal-case">(blank to keep current)</span>}
            </Label>
            <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)}
                   placeholder="••••••••••••" autoComplete="new-password"
                   data-testid="acc-password" className="mt-1.5 font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Schedule time</Label>
              <Input value={form.schedule_time} onChange={(e) => set("schedule_time", e.target.value)}
                     placeholder="22:00" data-testid="acc-schedule" className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Timezone</Label>
              <Input value={form.timezone} onChange={(e) => set("timezone", e.target.value)}
                     placeholder="Europe/London" data-testid="acc-timezone" className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Payments URL</Label>
            <Input value={form.payments_url} onChange={(e) => set("payments_url", e.target.value)}
                   data-testid="acc-payments-url" className="mt-1.5 text-xs font-mono" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                      rows={2} data-testid="acc-notes" className="mt-1.5" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-brand-border px-3 py-2">
            <div>
              <div className="text-sm font-medium">Enable automatic import</div>
              <div className="text-xs text-brand-text-muted">
                Runs daily at {form.schedule_time} ({form.timezone})
              </div>
            </div>
            <Switch checked={form.automation_enabled}
                    onCheckedChange={(v) => set("automation_enabled", v)}
                    data-testid="acc-automation-toggle" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="acc-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-brand-red hover:bg-brand-red-hover"
                  data-testid="acc-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            {editing ? "Save changes" : "Create account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ImportHistorySheet = ({ open, onOpenChange, accountId }) => {
  const { data: history, mutate: reload } = useSWR(
    open && accountId ? `/integrations/dpd/accounts/${accountId}/import-history` : null,
    fetcher
  );
  const { data: drivers } = useSWR(open ? "/drivers" : null, fetcher);
  const [deletingId, setDeletingId] = useState(null);
  const [assigning, setAssigning] = useState(null); // {code, importId}
  const [assignDriverId, setAssignDriverId] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);
  // Auto-suggest — fetched when the assign dialog opens with a code.
  const { data: suggestData, isLoading: suggestLoading } = useSWR(
    assigning?.code ? `/drivers/suggest-for-code?code=${encodeURIComponent(assigning.code)}&limit=5` : null,
    fetcher
  );
  const suggestions = suggestData?.suggestions || [];

  const doDelete = async (imp) => {
    if (!window.confirm(`Delete import "${imp.filename || imp.id}"?\n\nThis will remove the record AND rollback ${imp.row_count || 0} row(s). You'll be able to re-import the same file after this.`)) return;
    setDeletingId(imp.id);
    try {
      const { data } = await api.delete(`/integrations/dpd/imports/${imp.id}`);
      toast.success(`Deleted. Rolled back ${data.rows_removed} row(s).`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
    setDeletingId(null);
  };

  const doAssign = async ({ driverId, source = "manual", confidence } = {}) => {
    const target = driverId || assignDriverId;
    if (!target || !assigning?.code) return;
    setAssignBusy(true);
    try {
      const body = { code: assigning.code, source };
      if (typeof confidence === "number") body.confidence = confidence;
      await api.post(`/drivers/${target}/attach-code`, body);
      toast.success(
        `Code "${assigning.code}" now maps to the selected driver. Delete this import & re-run to link rows.`
      );
      setAssigning(null);
      setAssignDriverId("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    }
    setAssignBusy(false);
  };

  return (
    <><Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="import-history-dialog">
        <DialogHeader>
          <DialogTitle>Import history</DialogTitle>
          <DialogDescription>{(history || []).length} record(s)</DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto max-h-[60vh]">
          <table className="w-full text-sm">
            <thead className="bg-brand-surface text-brand-text-muted uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">File</th>
                <th className="text-right px-3 py-2">Inserted</th>
                <th className="text-right px-3 py-2">Skipped</th>
                <th className="text-left px-3 py-2">Trigger</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Unmatched driver codes</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {(history || []).map((h) => (
                <tr key={h.id}>
                  <td className="px-3 py-2 font-mono text-xs">{ddmmyyyy(h.created_at?.slice(0, 10))}</td>
                  <td className="px-3 py-2 text-xs">{h.filename || "—"}</td>
                  <td className="px-3 py-2 text-right font-medium text-emerald-700">{h.row_count || 0}</td>
                  <td className="px-3 py-2 text-right text-brand-text-muted">{h.skipped || 0}</td>
                  <td className="px-3 py-2 text-xs">{h.trigger_type}</td>
                  <td className="px-3 py-2 text-xs">
                    {h.error_code
                      ? <span className="text-red-600">{h.error_code}</span>
                      : <span className="text-emerald-700">{h.import_status}</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {h.unmatched_driver_codes && Object.keys(h.unmatched_driver_codes).length > 0
                      ? <div className="space-y-1">
                          {Object.entries(h.unmatched_driver_codes).map(([code, count]) => (
                            <div key={code} className="flex items-center gap-2 font-mono">
                              <span><b>{code}</b> <span className="text-brand-text-muted">×{count}</span></span>
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => { setAssigning({code, importId: h.id}); setAssignDriverId(""); }}
                                className="h-5 px-1.5 text-[10px] text-brand-navy hover:bg-brand-navy/10"
                                data-testid={`assign-code-${code}`}
                              >
                                → Assign
                              </Button>
                            </div>
                          ))}
                        </div>
                      : <span className="text-brand-text-muted">—</span>
                    }
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => doDelete(h)}
                      disabled={deletingId === h.id}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                      data-testid={`delete-import-${h.id}`}
                      title="Delete import & rollback rows"
                    >
                      {deletingId === h.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </td>
                </tr>
              ))}
              {(history || []).length === 0 && (
                <tr><td colSpan="8" className="text-center text-brand-text-muted py-8">No imports yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={!!assigning} onOpenChange={(o) => { if (!o) { setAssigning(null); setAssignDriverId(""); } }}>
      <DialogContent data-testid="assign-code-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign code to driver</DialogTitle>
          <DialogDescription>
            The code <b className="font-mono">{assigning?.code}</b> was unmatched
            in this import. Pick which driver it should map to — future imports
            carrying this code will link automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          {/* Auto-suggest — never auto-applies, always requires a click. */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-brand-text-muted">
                Suggested matches
              </Label>
              {suggestLoading && (
                <Loader2 className="w-3 h-3 text-brand-text-muted animate-spin" />
              )}
            </div>
            <div className="mt-1.5 space-y-1.5" data-testid="assign-suggestions">
              {(!suggestLoading && suggestions.length === 0) && (
                <div className="text-[11px] text-brand-text-muted italic">
                  No close matches — pick a driver manually below.
                </div>
              )}
              {suggestions.map((s) => {
                const pctInt = Math.round((s.confidence || 0) * 100);
                const bandColour = s.band === "high"
                  ? "border-emerald-300 bg-emerald-50"
                  : s.band === "suggested"
                  ? "border-amber-300 bg-amber-50"
                  : "border-brand-border bg-brand-surface";
                const bandLabel = s.band === "high"
                  ? "High confidence"
                  : s.band === "suggested"
                  ? "Suggested"
                  : "Low match";
                const bandTextColour = s.band === "high"
                  ? "text-emerald-700"
                  : s.band === "suggested"
                  ? "text-amber-700"
                  : "text-brand-text-muted";
                return (
                  <div key={s.driver_id}
                       className={`rounded-lg border ${bandColour} p-2 flex items-center gap-2`}
                       data-testid={`suggestion-${s.driver_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        <span className="font-mono">{s.driver_code || "—"}</span>
                        {" — "}
                        {s.full_name}
                      </div>
                      <div className={`text-[10px] ${bandTextColour} flex items-center gap-1.5 mt-0.5`}>
                        <span className="font-semibold uppercase tracking-wider">{bandLabel}</span>
                        <span className="font-mono">{pctInt}%</span>
                        <span className="text-brand-text-muted truncate">· {s.reason}</span>
                      </div>
                    </div>
                    <Button size="sm" variant="outline"
                            onClick={() => doAssign({
                              driverId: s.driver_id,
                              source: "suggestion",
                              confidence: s.confidence,
                            })}
                            disabled={assignBusy}
                            data-testid={`accept-suggestion-${s.driver_id}`}
                            className={s.band === "high"
                              ? "border-emerald-500 text-emerald-700 hover:bg-emerald-100"
                              : ""}>
                      {assignBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Accept"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs text-brand-text-muted">Or pick manually</Label>
            <select
              value={assignDriverId}
              onChange={(e) => setAssignDriverId(e.target.value)}
              data-testid="assign-driver-select"
              className="mt-1.5 w-full h-9 rounded-md border border-brand-border bg-white px-2 text-sm"
            >
              <option value="">— Select a driver —</option>
              {(drivers || []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.driver_code} — {d.full_name}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-brand-text-muted mt-2">
              Tip: after assigning, delete this import row and press
              &ldquo;Import full history&rdquo; (or &ldquo;Run import now&rdquo;) to re-import — the
              now-mapped rows will attach to the driver. Historical payroll is
              never rewritten automatically.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setAssigning(null)}>Cancel</Button>
          <Button onClick={() => doAssign()} disabled={!assignDriverId || assignBusy}
                   className="bg-brand-red hover:bg-brand-red-hover"
                   data-testid="assign-confirm">
            {assignBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            Assign manually
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

const AccountCard = ({ account, onRefetch, onEdit, onHistory }) => {
  const [running, setRunning] = useState({ test: false, run: false, refresh: false, all: false });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const uploadFileRef = useRef(null);
  const set = (k, v) => setRunning((r) => ({ ...r, [k]: v }));

  const doTest = async () => {
    set("test", true);
    try {
      await api.post(`/integrations/dpd/accounts/${account.id}/test`);
      toast.success(`${account.account_name}: test connection started — status will update shortly`);
      onRefetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start test");
    }
    set("test", false);
  };
  const doRun = async () => {
    set("run", true);
    try {
      await api.post(`/integrations/dpd/accounts/${account.id}/run`);
      toast.success(`${account.account_name}: import started in background`);
      onRefetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start import");
    }
    set("run", false);
  };
  const doBackfillAll = async () => {
    set("all", true);
    try {
      await api.post(`/integrations/dpd/accounts/${account.id}/backfill-all`);
      toast.success(`${account.account_name}: importing every period on DPD portal. Check History for progress.`);
      setConfirmAll(false);
      onRefetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start full backfill");
    }
    set("all", false);
  };
  const doRefresh = async () => {
    set("refresh", true);
    try {
      await api.post(`/integrations/dpd/accounts/${account.id}/refresh-session`);
      toast.success("Refresh started — status will update shortly");
      onRefetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    }
    set("refresh", false);
  };
  const doDelete = async () => {
    try {
      await api.delete(`/integrations/dpd/accounts/${account.id}`);
      toast.success("Account deleted (import history preserved)");
      setConfirmDelete(false);
      onRefetch();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const doUploadCsv = async () => {
    const f = uploadFileRef.current?.files?.[0];
    if (!f) {
      toast.error("Choose a DPD CSV or Excel file first");
      return;
    }
    setUploadBusy(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(
        `/integrations/dpd/accounts/${account.id}/upload-csv`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setUploadResult(data);
      if (data.duplicate) {
        toast.info("File already imported — no rows added.");
      } else {
        toast.success(data.message || `Imported ${data.inserted || 0} row(s).`);
      }
      onRefetch();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed";
      setUploadResult({ ok: false, error: msg });
      toast.error(msg);
    }
    setUploadBusy(false);
  };

  const openInteractiveLogin = () => {
    // Named target: repeated clicks focus the same tab instead of opening duplicates.
    const tab = window.open(`/admin/integrations/dpd/${account.id}/manual-login`, `dpd-manual-login-${account.id}`);
    if (tab) tab.focus();
  };
  const cancelInteractiveLogin = async () => {
    try {
      await api.post(`/integrations/dpd/accounts/${account.id}/manual-login/cancel`);
      toast.success("Interactive login closed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
    onRefetch();
  };
  const isRunning = account.connection_status === "Running";
  const manualOpen = !!account.manual_login_active;
  const showError = account.last_error_code && !isRunning;

  return (
    <Card className="p-5 rounded-2xl border-brand-border" data-testid={`dpd-account-card-${account.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading font-bold text-brand-text truncate">{account.account_name}</div>
          <div className="text-xs text-brand-text-muted mt-0.5 font-mono">{account.username_masked}</div>
        </div>
        <StatusBadge status={account.connection_status} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Schedule</div>
          <div className="font-mono mt-0.5">
            {account.schedule_time} {account.timezone?.split("/")?.[1] || account.timezone}
            {account.automation_enabled ? "" : " · off"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Last login</div>
          <div className="font-mono mt-0.5">{account.last_successful_login_at ? ddmmyyyy(account.last_successful_login_at.slice(0,10)) : "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Last download</div>
          <div className="font-mono mt-0.5">{account.last_successful_download_at ? ddmmyyyy(account.last_successful_download_at.slice(0,10)) : "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Last import</div>
          <div className="font-mono mt-0.5">{account.last_import_at ? ddmmyyyy(account.last_import_at.slice(0,10)) : "—"}</div>
        </div>
      </div>

      {manualOpen && (
        <div className="mt-3 p-2 rounded-lg border border-blue-200 bg-blue-50 text-xs text-blue-800"
             data-testid={`manual-login-open-${account.id}`}>
          <div>Interactive DPD login is open in another tab. Test, Run and Full history are paused until it finishes.</div>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="outline" onClick={openInteractiveLogin} className="h-7 text-xs"
                    data-testid={`manual-login-goto-${account.id}`}>Go to login tab</Button>
            <Button size="sm" variant="outline" onClick={cancelInteractiveLogin} className="h-7 text-xs border-blue-300"
                    data-testid={`manual-login-cancel-card-${account.id}`}>Cancel interactive login</Button>
          </div>
        </div>
      )}

      {showError && (
        <div className="mt-3 p-2 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700"
             data-testid={`error-${account.id}`}>
          <div><b>{account.last_error_code}</b>{account.last_error_message ? ` — ${account.last_error_message}` : ""}</div>
          {account.last_error_code === "DPD_CREDENTIALS_REJECTED" && (
            <div className="mt-1 font-medium">DPD rejected the saved password. Edit this account and save the current DPD password; automatic runs are paused until then.</div>
          )}
          {account.last_error_code === "DPD_JOB_TIMEOUT" && (
            <div className="mt-1 font-medium">The job was stopped safely instead of remaining stuck. Check the diagnostic, then retry.</div>
          )}
          {account.diagnostic_captured_at && (
            <a
              href={`${api.defaults.baseURL}/integrations/dpd/accounts/${account.id}/diagnostic-screenshot?ts=${account.diagnostic_captured_at}`}
              target="_blank" rel="noopener noreferrer"
              className="mt-1.5 inline-flex items-center gap-1 underline hover:no-underline"
              data-testid={`diag-${account.id}`}
            >
              <ScanEye className="w-3 h-3" /> View what DPD showed
            </a>
          )}
        </div>
      )}

      {account.needs_manual_action && !isRunning && !manualOpen && (
        <div className="mt-3 p-3 rounded-lg border border-amber-300 bg-amber-50"
             data-testid={`fallback-banner-${account.id}`}>
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-amber-900">
                Authentication required
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                Automatic DPD login could not continue. Use one of the manual
                fallbacks below so payroll imports don&rsquo;t stall.
              </div>
              <div className="mt-2.5 flex flex-wrap gap-2">
                <Button size="sm"
                        onClick={openInteractiveLogin}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid={`manual-reconnect-${account.id}`}>
                  <KeyRound className="w-3.5 h-3.5 mr-1.5" />
                  Open interactive DPD login
                </Button>
                <Button size="sm" variant="outline"
                        onClick={() => { setUploadResult(null); setUploadOpen(true); }}
                        className="border-amber-600 text-amber-800 hover:bg-amber-100"
                        data-testid={`upload-csv-banner-${account.id}`}>
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  Upload DPD CSV
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={doTest} disabled={running.test || manualOpen}
                data-testid={`test-${account.id}`}>
          {running.test ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <TestTube2 className="w-3.5 h-3.5 mr-1.5" />}
          Test connection
        </Button>
        <Button size="sm" className="bg-brand-red hover:bg-brand-red-hover" onClick={doRun} disabled={running.run || manualOpen}
                data-testid={`run-${account.id}`}>
          {running.run ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
          Run import now
        </Button>
        <Button size="sm" variant="outline" onClick={() => setConfirmAll(true)} disabled={running.all || manualOpen}
                data-testid={`backfill-all-${account.id}`}
                className="border-brand-navy text-brand-navy hover:bg-brand-navy/10">
          {running.all ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
          Import full history
        </Button>
        <Button size="sm" variant="outline" onClick={doRefresh} disabled={running.refresh || manualOpen}
                data-testid={`refresh-${account.id}`}>
          {running.refresh ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          Refresh session
        </Button>
        {!(account.needs_manual_action && !isRunning && !manualOpen) && (
          <Button size="sm" variant="outline" onClick={openInteractiveLogin} disabled={manualOpen || isRunning}
                  data-testid={`interactive-login-${account.id}`}>
            <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Open interactive DPD login
          </Button>
        )}
        <Button size="sm" variant="outline"
                onClick={() => { setUploadResult(null); setUploadOpen(true); }}
                data-testid={`upload-csv-${account.id}`}
                title="Manually upload a DPD payment CSV/Excel — bypasses automation, feeds the same import pipeline.">
          <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload CSV
        </Button>
        <Button size="sm" variant="outline" onClick={() => onHistory(account)}
                data-testid={`history-${account.id}`}>
          <HistoryIcon className="w-3.5 h-3.5 mr-1.5" /> History
        </Button>
        <Button size="sm" variant="outline" onClick={() => onEdit(account)}
                data-testid={`edit-${account.id}`}>
          <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}
                data-testid={`delete-${account.id}`}>
          <Trash2 className="w-3.5 h-3.5 mr-1.5 text-red-600" />
        </Button>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this DPD account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will disable future imports for <b>{account.account_name}</b>. Existing import history will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700"
                                data-testid="confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmAll} onOpenChange={setConfirmAll}>
        <AlertDialogContent data-testid="backfill-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Import full DPD history?</AlertDialogTitle>
            <AlertDialogDescription>
              This will log in to <b>{account.account_name}</b> and download EVERY
              period visible on the DPD Payments page (all years, all 4-week
              cycles), importing each one into your system. Files already
              imported are skipped automatically (duplicate detection by hash).
              <br /><br />
              This runs in the background — it can take several minutes. Check
              the History button for per-period status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doBackfillAll} className="bg-brand-navy hover:bg-brand-navy/90"
                                data-testid="confirm-backfill-all">Start full import</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual CSV upload — bypasses automation, uses same pipeline as automated download. */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { setUploadOpen(o); if (!o) { setUploadResult(null); if (uploadFileRef.current) uploadFileRef.current.value = ""; } }}>
        <DialogContent data-testid="upload-csv-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-brand-navy" />
              Upload DPD CSV
            </DialogTitle>
            <DialogDescription>
              Download the Payment Report from the DPD portal manually and
              upload it here. The file feeds into the same
              validate → duplicate-check → driver-match → pay-period-match
              → process pipeline the automation uses. Payroll math is
              identical.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">DPD file (.csv or .xlsx)</Label>
              <Input type="file" accept=".csv,.xlsx,.xls" ref={uploadFileRef}
                     data-testid={`upload-file-input-${account.id}`}
                     className="mt-1.5" disabled={uploadBusy} />
              <p className="text-[11px] text-brand-text-muted mt-1.5">
                Max 25 MB. Duplicates are detected by file hash — re-uploading
                the same file will not create duplicate payroll rows.
              </p>
            </div>

            {uploadResult && (
              <div
                className={
                  "rounded-lg border p-3 text-xs " +
                  (uploadResult.duplicate
                    ? "border-amber-300 bg-amber-50 text-amber-900"
                    : uploadResult.error
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-emerald-200 bg-emerald-50 text-emerald-800")
                }
                data-testid={`upload-result-${account.id}`}
              >
                {uploadResult.error && <div><b>Error:</b> {uploadResult.error}</div>}
                {!uploadResult.error && (
                  <>
                    <div className="font-semibold">
                      {uploadResult.duplicate ? "Duplicate file — nothing to import" : "Import complete"}
                    </div>
                    <div className="mt-1 space-y-0.5 font-mono">
                      {typeof uploadResult.inserted === "number" && (
                        <div>Inserted: <b>{uploadResult.inserted}</b></div>
                      )}
                      {typeof uploadResult.skipped === "number" && uploadResult.skipped > 0 && (
                        <div>Skipped: {uploadResult.skipped}</div>
                      )}
                      {uploadResult.unmatched_driver_codes &&
                       Object.keys(uploadResult.unmatched_driver_codes).length > 0 && (
                        <div>
                          Unmatched driver codes:{" "}
                          {Object.entries(uploadResult.unmatched_driver_codes)
                            .map(([c, n]) => `${c}×${n}`).join(", ")}
                          <div className="mt-1 text-[11px] font-sans text-brand-text-muted normal-case">
                            → Assign these codes to drivers from the History dialog, then re-upload.
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline"
                    onClick={() => setUploadOpen(false)}
                    disabled={uploadBusy}
                    data-testid={`upload-cancel-${account.id}`}>
              Close
            </Button>
            <Button onClick={doUploadCsv} disabled={uploadBusy}
                    className="bg-brand-red hover:bg-brand-red-hover"
                    data-testid={`upload-submit-${account.id}`}>
              {uploadBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              {uploadBusy ? "Uploading…" : "Upload & import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const ManualLoginReturnBanner = ({ accounts, onRefetch }) => {
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const accountId = params.get("account");
  if (params.get("manual_login") !== "success" || !accountId || !accounts) return null;
  const account = accounts.find((a) => a.id === accountId);
  const dismiss = () => setParams({}, { replace: true });
  const runNow = async () => {
    setBusy(true);
    try {
      await api.post(`/integrations/dpd/accounts/${accountId}/run`);
      toast.success("Import started in background");
      dismiss();
      onRefetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start import");
    }
    setBusy(false);
  };
  return (
    <Card className="p-4 rounded-2xl border-emerald-200 bg-emerald-50 flex items-center justify-between gap-3 flex-wrap"
          data-testid="manual-login-return-banner">
      <div className="flex items-center gap-2 text-sm text-emerald-900">
        <CheckCircle2 className="w-4 h-4" />
        DPD login saved for <b>{account?.account_name || "this account"}</b>. No import has run yet.
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={runNow} disabled={busy} className="bg-brand-red hover:bg-brand-red-hover"
                data-testid="manual-login-run-now">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
          Run import now
        </Button>
        <Button size="sm" variant="outline" onClick={dismiss} data-testid="manual-login-dismiss">Later</Button>
      </div>
    </Card>
  );
};

const DpdAccounts = () => {
  const { user } = useAuth();
  const { data: accounts, mutate: reload } = useSWR("/integrations/dpd/accounts", fetcher, {
    refreshInterval: 5000, // auto-poll while a background job (test/run/refresh) is in flight
  });
  const { data: companies } = useSWR(user?.role === "platform_owner" ? "/companies" : null, fetcher);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyAccount, setHistoryAccount] = useState(null);

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Integrations · DPD Portal</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">DPD Accounts</h1>
          <p className="text-sm text-brand-text-muted mt-1 max-w-2xl">
            Automate daily Payment Report downloads. Each account has its own credentials, schedule and session — you can add as many accounts as your company operates.
          </p>
        </div>
        <div className="flex gap-2">
          {user?.role === "platform_owner" && (
            <Link to="/admin/integrations/dpd/selectors">
              <Button variant="outline" data-testid="open-selectors">
                <ScanEye className="w-4 h-4 mr-1.5" /> Advanced (Selectors)
              </Button>
            </Link>
          )}
          <Button onClick={() => { setEditing(null); setFormOpen(true); }}
                  className="bg-brand-red hover:bg-brand-red-hover"
                  data-testid="add-account-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add DPD Account
          </Button>
        </div>
      </div>

      <ManualLoginReturnBanner accounts={accounts} onRefetch={reload} />

      <div className="grid md:grid-cols-2 gap-4" data-testid="accounts-grid">
        {(accounts || []).map((a) => (
          <AccountCard key={a.id} account={a} onRefetch={reload}
                        onEdit={(acc) => { setEditing(acc); setFormOpen(true); }}
                        onHistory={(acc) => setHistoryAccount(acc)} />
        ))}
        {accounts && accounts.length === 0 && (
          <Card className="p-8 rounded-2xl border-brand-border text-center text-sm text-brand-text-muted col-span-2"
                data-testid="no-accounts">
            <Radio className="w-8 h-8 mx-auto text-brand-text-muted opacity-50 mb-2" />
            No DPD accounts yet. Click <b>Add DPD Account</b> to connect your first portal.
          </Card>
        )}
      </div>

      <AccountForm
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        onSaved={() => reload()}
        user={user}
        companies={companies}
      />
      <ImportHistorySheet
        open={!!historyAccount}
        onOpenChange={(o) => { if (!o) setHistoryAccount(null); }}
        accountId={historyAccount?.id}
      />
    </div>
  );
};

export default DpdAccounts;
